/**
 * Centralised Axios/FastAPI error formatter.
 *
 * FastAPI returns two distinct `detail` shapes:
 *   - HTTPException: `detail` is a string.
 *   - Pydantic validation failure (422): `detail` is an array of
 *     `{type, loc, msg, input, url, ctx?}` objects.
 *
 * Rendering the array directly into React children or sonner toasts crashes
 * with "Objects are not valid as a React child". Always pipe error payloads
 * through `formatApiError` before surfacing them to the user.
 */
export function formatApiError(err, fallback = "Something went wrong") {
  const data = err?.response?.data;
  if (!data) return err?.message || fallback;

  const detail = data.detail;

  // 422 — Pydantic validation error: detail is an array of objects.
  if (Array.isArray(detail)) {
    return (
      detail
        .map((e) => {
          if (!e) return "";
          if (typeof e === "string") return e;
          const loc = Array.isArray(e.loc) ? e.loc.slice(1).join(".") : "";
          const msg = e.msg || e.message || "invalid";
          return loc ? `${loc}: ${msg}` : msg;
        })
        .filter(Boolean)
        .join("; ") || fallback
    );
  }

  // Standard HTTPException shape.
  if (typeof detail === "string") return detail;

  // Some endpoints return `{message: "..."}` or similar.
  if (typeof data.message === "string") return data.message;
  if (typeof data.error === "string") return data.error;

  // Last-resort: stringify but never return an object to React.
  try {
    return JSON.stringify(data);
  } catch {
    return fallback;
  }
}
